<template>
  <div :class="[isFullscreenRegistration ? 'loginimg' : 'white-background', dialogMode ? 'dialog-mode' : '']">
    <!-- Logo for fullscreen registration mode -->

    <CustomerLogo v-if="isFullscreenRegistration && !showSuccess && !showCheckout"/>

    <div :class="dialogMode ? '' : 'row justify-content-center'">
      <div :class="[mode === 'add-licenses' ? 'mt-6' : 'mt-10', dialogMode ? 'dialog-content-container' : 'col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 justify-content-center row']" :style="dialogMode ? 'width: 100%;' : 'align-self: center;'">

        <!-- Success/Loading/Error State -->
        <div v-if="showSuccess" class="col-12 text-center">
          <!-- Loading State -->
          <div v-if="isLoading">
            <v-progress-circular
              indeterminate
              :color="isFullscreenRegistration ? 'white' : 'black'"
              size="64"
              width="6"
              class="mb-6">
            </v-progress-circular>
            <div :class="textColorClass" class="mb-2" style="font-size: 18px; font-weight: 500;">
              {{ $t('verifying_payment') }}
            </div>
            <div :class="textColorClass" style="opacity: 0.7; font-size: 14px;">
              {{ $t('please_wait') }}...
            </div>
          </div>

          <!-- Success State -->
          <div v-else-if="success">
            <v-icon :color="isFullscreenRegistration ? 'white' : '#4CAF50'" size="80" class="mb-6">
              mdi-check-circle
            </v-icon>
            <div :class="textColorClass" class="text-h4 mb-4">
              {{ $t('payment_successful') }}
            </div>
            <div :class="textColorClass" class="col-10 mx-auto mb-6" style="opacity: 0.85; font-size: 16px;">
              {{ $t('subscription_success_assignment_info') }}
            </div>
            
            <div class="col-10 mx-auto mt-6">
              <v-btn
                class="white--text px-8"
                style="background-color: #161616 !important;"
                large
                tile
                @click="tryAgain">
                {{ $t('continue') }}
              </v-btn>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error">
            <v-icon color="#ff6363" size="80" class="mb-6">
              mdi-alert-circle
            </v-icon>
            <div :class="textColorClass" class="text-h4 mb-4">
              {{ $t('payment_failed') }}
            </div>
            <div :class="textColorClass" class="col-10 mx-auto mb-8" style="opacity: 0.85; font-size: 16px;">
              {{ errorMessage }}
            </div>

            <div class="col-10 mx-auto">
              <v-btn
                class="signinbtn white--text w-100"
                style="padding: 27px !important; background-color: #161616 !important;"
                outlined
                tile
                @click="showSuccess = false; showCheckout = false;">
                {{ $t('try_again') }}
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Checkout Container -->
        <div v-else-if="showCheckout" class="col-12">
          <!-- Back/Cancel Button -->
          <div class="mb-4">
            <v-btn
              text
              :class="textColorClass"
              class="pa-0"
              style="text-transform: none;"
              @click="cancelCheckout">
              <v-icon left small>mdi-arrow-left</v-icon>
              {{ $t('back') }}
            </v-btn>
          </div>
          <div id="checkout" class="white pa-4" style="min-height: 400px; border-radius: 4px;">
            <!-- Stripe Embedded Checkout will be mounted here -->
          </div>
        </div>

        <template v-else>
        <!-- White backdrop container for fullscreen registration mode -->
        <div :class="isFullscreenRegistration ? 'white-backdrop-card col-12' : 'col-12'">
        <!-- Title -->
        <div :class="dialogMode ? '' : ''" class="black--text text-h4 text-center">
          {{ mode === 'registration' ? $t('choose_your_plan') : $t('add_more_licenses') }}
        </div>

        <!-- Description -->
        <div class="black--text text-center mb-8 mt-4" style="opacity: 0.85; font-size: 14px;">
          {{ $t('subscription_plan_description') }}
        </div>

        <!-- Annual License Notice -->
        <div class="mb-6 pa-3" style="background-color: #E3F2FD; border-left: 4px solid #2196F3; border-radius: 4px;">
          <div class="d-flex align-start">
            <v-icon color="#2196F3" size="20" class="mr-2 mt-1">mdi-information</v-icon>
            <div style="font-size: 13px; color: #424242; line-height: 1.5;">
              {{ $t('annual_license_commitment') }}
            </div>
          </div>
        </div>

        <!-- Plan Selection Buttons -->
        <div class="mb-8 pa-0">
          <div class="black--text mb-3 text-uppercase"
               style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
            {{ $t('payment_frequency') }}
          </div>

          <div class="d-flex plan-buttons-container">
            <!-- Monthly Plan Button -->
            <v-btn
              class="rounded-0 white black--text plan-selector-btn"
              :class="selectedPlan === 'monthly' ? 'selected-plan' : ''"
              tile
              elevation="0"
              @click="selectedPlan = 'monthly'">
              <!-- Selection Indicator Dot -->
              <div class="selection-dot" :class="selectedPlan === 'monthly' ? 'selected' : ''"></div>
              <div class="d-flex flex-column align-center justify-center plan-content">
                <div class="plan-label" style="font-size: 11px; font-weight: 600;">{{ $t('monthly') }}</div>
                <div class="plan-price">{{ pricing.monthly.pricePerLicense }} <span v-if="pricing.monthly.pricePerLicense !== '-'">€</span></div>
                <div class="plan-period">{{ $t('per_license_per_month') }}</div>
                <div class="plan-description">{{ $t('monthly_payment_desc') }}</div>
              </div>
            </v-btn>

            <!-- Yearly Plan Button -->
            <v-btn
              class="rounded-0 white black--text plan-selector-btn"
              :class="selectedPlan === 'yearly' ? 'selected-plan' : ''"
              tile
              elevation="0"
              @click="selectedPlan = 'yearly'">
              <!-- Selection Indicator Dot -->
              <div class="selection-dot" :class="selectedPlan === 'yearly' ? 'selected' : ''"></div>
              <div class="d-flex flex-column align-center justify-center plan-content">
                <div class="plan-label" style="font-size: 11px; font-weight: 600;">{{ $t('yearly') }}</div>
                <div class="plan-price">{{ pricing.yearly.pricePerLicense }} <span v-if="pricing.yearly.pricePerLicense !== '-'">€</span></div>
                <div class="plan-period">{{ $t('per_license_per_year') }}</div>
                <div class="plan-description">{{ $t('yearly_payment_desc') }}</div>
              </div>
            </v-btn>
          </div>
        </div>

        <!-- License Count Selector -->
        <div class="mb-6 pa-0">
          <div class="black--text mb-3 text-uppercase"
               style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
            {{ $t('number_of_licenses') }}
          </div>
          <div class="d-flex align-center rounded-0" style="gap: 12px; height: 54px; max-width: 280px;">


            <v-btn
              icon
              large
              outlined
              tile
              class="rounded-0"
              @click="decrementLicenses"
              :disabled="licenseCount <= 1"
              style="border: 1px solid #bdbdbd; top: -3px; background-color: white;">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <v-text-field
              v-model.number="licenseCount"
              type="number"
              min="1"
              dense
              outlined
              hide-details
              class="text-center rounded-0"
              style="max-width: 80px;">
            </v-text-field>
            <v-btn
              icon
              large
              outlined
              tile
              class="rounded-0 "
              @click="incrementLicenses"
              style="border: 1px solid #bdbdbd; top: -3px; background-color: white;">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Price Summary -->
        <div class="mb-6 pa-0">
          <div class="black--text mb-3 text-uppercase"
               style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
            {{ $t('price_overview') }}
          </div>
          <div class="rounded-0 pa-4" style="border: 1px solid #e0e0e0;">
            <div class="d-flex justify-space-between mb-2" style="font-size: 14px;">
              <span style="color: #424242;">{{ $t('price_per_license') }}</span>
              <span class="font-weight-bold">{{ pricePerLicense }}<span v-if="pricePerLicense !== '-'">€</span></span>
            </div>
            <div class="d-flex justify-space-between mb-2" style="font-size: 14px;">
              <span style="color: #424242;">{{ $t('number_of_licenses') }}</span>
              <span class="font-weight-bold">{{ licenseCount }}</span>
            </div>
            <div v-if="selectedPlan === 'monthly'" class="d-flex justify-space-between mb-2" style="font-size: 14px;">
              <span style="color: #424242;">{{ $t('minimum_term_total') }}</span>
              <span class="font-weight-bold">{{ minimumTermTotalPrice }}<span v-if="minimumTermTotalPrice !== '-'">€</span></span>
            </div>
            <v-divider class="my-2" style="border-color: #bdbdbd;"></v-divider>
            <div class="d-flex justify-space-between align-center">
              <span style="font-size: 16px; font-weight: bold;">Total</span>
              <span style="font-size: 20px; font-weight: bold;">{{ totalPrice }}<span v-if="totalPrice !== '-'">€</span></span>
            </div>
          </div>
        </div>

        <!-- Proceed to Checkout Button -->
        <div class="pa-0 mb-4 d-flex justify-end">
          <v-btn
            class="white--text"
            :class="licenseCount >= 1 && !isLoading ? 'signinbtn' : ''"
            :style="licenseCount >= 1 && !isLoading ? 'padding: 12px 24px !important; background-color: #161616 !important;' : 'padding: 12px 24px !important; background-color: #666666 !important; border: 1px solid #666666 !important;'"
            outlined
            tile
            @click="proceedToCheckout"
            :loading="isLoading"
            :disabled="licenseCount < 1">
            {{ $t('proceed_to_checkout') }}
          </v-btn>
        </div>

        <!-- Skip Button (only for registration mode) -->
        <div v-if="mode === 'registration'" class="pa-0 mb-0 text-center">
          <v-btn
            text
            class="black--text"
            style="text-transform: none; font-size: 14px; text-decoration: underline;"
            @click="skipForNow">
            {{ $t('skip_for_now') }}
          </v-btn>
        </div>
        </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import CustomerLogo from "@/components/custom/CustomerLogo.vue";

export default {
  name: 'SubscriptionPlans',
  components: {CustomerLogo},

  props: {
    modeProp: {
      type: String,
      default: null
    },
    dialogMode: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedPlan: 'monthly',
      licenseCount: 1,
      isLoading: false,
      me: null,
      showCheckout: false,
      showSuccess: false,
      success: false,
      error: false,
      errorMessage: '',
      redirectProgress: 0,
      redirectTimer: null,

      // Mock pricing data - will be replaced with API call later
      pricing: {
        monthly: {
          pricePerLicense: '-'
        },
        yearly: {
          pricePerLicense: '-'
        }
      },
      currentSessionId: null,
      stripeCheckout: null
    };
  },

  beforeDestroy() {
    // Clean up Stripe checkout instance
    if (this.stripeCheckout) {
      this.stripeCheckout.destroy();
      this.stripeCheckout = null;
    }

    if (this.currentSessionId && !this.success) {
      // Expire the session if we are leaving and payment wasn't successful
      this.$axios.post(`api/payment/session/${this.currentSessionId}/expire`).catch(err => {
         console.warn('Failed to expire session:', err);
      });
    }
  },

  mounted() {
    this.fetchPricing();
    this.fetchMe();
    if (this.$route.query.session_id) {
      this.showSuccess = true;
      this.verifyPayment(this.$route.query.session_id);
    }
  },

  computed: {
    mode() {
      return this.modeProp || this.$route.query.mode || 'registration';
    },

    // Determines if we're in fullscreen registration mode (not dialog mode)
    isFullscreenRegistration() {
      return this.mode === 'registration' && !this.dialogMode;
    },

    // Returns the appropriate text color class based on the mode
    textColorClass() {
      return this.isFullscreenRegistration ? 'white--text' : 'black--text';
    },

    pricePerLicense() {
      if (this.pricing[this.selectedPlan].pricePerLicense === '-') {
        return '-';
      }
      return this.pricing[this.selectedPlan].pricePerLicense;
    },

    totalPrice() {
      if (this.pricePerLicense === '-') {
        return '-';
      }
      return this.pricePerLicense * this.licenseCount;
    },

    minimumTermTotalPrice() {
      if (this.pricePerLicense === '-') {
        return '-';
      }
      return this.pricePerLicense * this.licenseCount * 12;
    }
  },

  methods: {
    async fetchPricing() {
      try {
        const response = await this.$axios.get('api/payment/pricing');
        this.pricing = {
          monthly: {
            pricePerLicense: response.data.monthly.pricePerLicense / 100  // Convert cents to euros
          },
          yearly: {
            pricePerLicense: response.data.yearly.pricePerLicense / 100
          }
        };
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Keep fallback values
      }
    },

    async fetchMe() {
      try {
        const response = await this.$axios.get('api/me');
        this.me = response.data;
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    },

    incrementLicenses() {
      this.licenseCount++;
    },

    decrementLicenses() {
      if (this.licenseCount > 1) {
        this.licenseCount--;
      }
    },

    async proceedToCheckout() {
      if (this.licenseCount < 1) {
        this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('minimum_one_license_required'));
        return;
      }

      if (!this.me || !this.me.customer || !this.me.customer.id) {
        this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>Kundeninformation fehlt');
        return;
      }

      this.isLoading = true;

      try {
        const customerId = this.me.customer.id;
        const plan = this.selectedPlan; // 'monthly' or 'yearly'
        const returnUrl = window.location.origin + window.location.pathname;

        // Call the backend to create a Stripe checkout session
        const response = await this.$axios.get(
          `api/payment/customer/${customerId}/checkout/${plan}`,
          { params: { licenses: this.licenseCount, returnUrl } }
        );

        const checkoutData = response.data;

        // Store checkout info for potential recovery
        sessionStorage.setItem('checkoutInfo', JSON.stringify({
          sessionId: checkoutData.sessionId,
          customerId: checkoutData.customerId,
          plan: checkoutData.plan,
          licenses: checkoutData.licenses
        }));

        // Initialize Stripe embedded checkout
        if (checkoutData.clientSecret) {
          await this.initializeStripeEmbeddedCheckout(checkoutData);
        } else {
          throw new Error('No client secret received');
        }

      } catch (error) {
        console.error('Checkout creation failed:', error);

        // Show more specific error message if available
        if (error.response && error.response.data) {
          const errorData = error.response.data;

          if (errorData.error === 'STRIPE_NOT_CONFIGURED' || errorData.error === 'STRIPE_PRICE_NOT_CONFIGURED') {
            this.$noty.error('<i class="fas fa-exclamation-triangle mr-3"></i>' + errorData.message);
          } else if (errorData.message) {
            this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + errorData.message);
          } else {
            this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('checkout_creation_failed'));
          }
        } else {
          this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('checkout_creation_failed'));
        }

        this.isLoading = false;
      }
    },

    async loadStripeScript() {
      // Check if Stripe is already loaded
      if (window.Stripe) {
        return Promise.resolve();
      }

      // Check if script is already being loaded
      if (document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
        return new Promise((resolve) => {
          const checkStripe = setInterval(() => {
            if (window.Stripe) {
              clearInterval(checkStripe);
              resolve();
            }
          }, 100);
        });
      }

      // Load Stripe script dynamically
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Stripe script'));
        document.head.appendChild(script);
      });
    },

    async initializeStripeEmbeddedCheckout(checkoutData) {
      this.showCheckout = true;
      this.isLoading = false;
      this.currentSessionId = checkoutData.sessionId;

      // Wait for DOM update to ensure #checkout element exists
      await this.$nextTick();

      // Load Stripe script if not already loaded
      await this.loadStripeScript();

      // Initialize Stripe
      // eslint-disable-next-line no-undef
      const stripe = Stripe(checkoutData.stripePublicKey);

      // Initialize the embedded checkout
      const checkout = await stripe.initEmbeddedCheckout({
        clientSecret: checkoutData.clientSecret,
      });

      // Store checkout instance for cleanup
      this.stripeCheckout = checkout;

      // Mount the checkout
      checkout.mount('#checkout');
    },

    async cancelCheckout() {
      // Destroy the Stripe checkout if it exists
      if (this.stripeCheckout) {
        this.stripeCheckout.destroy();
        this.stripeCheckout = null;
      }

      // Expire the session on the backend
      if (this.currentSessionId) {
        try {
          await this.$axios.post(`api/payment/session/${this.currentSessionId}/expire`);
        } catch (err) {
          console.warn('Failed to expire session:', err);
        }
        this.currentSessionId = null;
      }

      // Go back to plan selection
      this.showCheckout = false;
    },

    skipForNow() {
      // Skip subscription and go to licenses page
      this.$router.push('/licenses');
    },

    verifyPayment(sessionId) {
      this.isLoading = true;

      this.$axios.get(`api/payment/check-payment-status/session/${sessionId}`)
        .then(() => {
          this.success = true;
          this.isLoading = false;
          // this.startRedirectTimer();
        })
        .catch(error => {
          console.error('Payment verification error:', error);
          this.error = true;
          this.isLoading = false;
          this.errorMessage = this.$t('payment_verification_failed');
        });
    },

    startRedirectTimer() {
      const duration = 3000; // 3 seconds
      const interval = 50;
      const steps = duration / interval;
      let currentStep = 0;

      this.redirectTimer = setInterval(() => {
        currentStep++;
        this.redirectProgress = (currentStep / steps) * 100;

        if (currentStep >= steps) {
          clearInterval(this.redirectTimer);
          this.tryAgain(); // Redirects to login or dashboard
        }
      }, interval);
    },

    tryAgain() {
      // Clear query params first (important for both modes)
      this.$router.replace({ query: null }).catch(() => {});

      // In dialog mode, just emit close event to close the dialog
      if (this.dialogMode) {
        this.$emit('close');
        return;
      }

      // Only redirect to licenses page in fullscreen mode
      this.$router.push('/licenses').catch(() => {});
    }
  }
};
</script>

<style scoped>
.white-background {
  background-color: white;
  min-height: 100vh;
  padding: 20px 16px;
}

.white-background.dialog-mode {
  min-height: auto;
  padding: 0;
}

/* White backdrop card for fullscreen registration mode */
.white-backdrop-card {
  background-color: white;
  padding: 48px 40px;
  border-radius: 4px;
  max-width: 480px;
  margin: 0 auto;
}

.dialog-content-container {
  padding: 0 32px;
  width: 100%;
}

/* Plan Buttons Container */
.plan-buttons-container {
  gap: 12px;
  flex-wrap: wrap;
}

/* Plan Selector Button Styles */
.plan-selector-btn {
  border: 2px solid #e0e0e0 !important;
  background-color: #f5f5f5 !important;
  transition: all 0.3s ease;
  height: auto !important;
  min-height: 116px !important;
  text-transform: none !important;
  padding: 18px 12px 14px !important;
  flex: 1 1 0;
  min-width: 140px;
  position: relative;
  white-space: normal !important;
}

.plan-selector-btn::v-deep .v-btn__content {
  width: 100%;
  min-width: 0;
  white-space: normal;
}

.plan-selector-btn.selected-plan {
  border: 2px solid #161616 !important;
  background-color: white !important;
}

/* Plan Content Styles */
.plan-content {
  width: 100%;
  min-width: 0;
}

.plan-label {
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 4px;
}

.plan-price {
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
}

.plan-period {
  font-size: 10px;
  opacity: 0.7;
  margin-top: 4px;
}

.plan-description {
  font-size: 9px;
  opacity: 0.6;
  margin-top: 6px;
  line-height: 1.3;
  white-space: normal;
  overflow-wrap: anywhere;
  max-width: 100%;
}

/* Selection Indicator Dot */
.selection-dot {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #666666;
  background-color: transparent;
  transition: all 0.3s ease;
}

.selection-dot.selected {
  border-color: #161616;
  background-color: #161616;
  box-shadow: inset 0 0 0 3px white;
}

/* Save Badge - New Style */
.save-badge-new {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  background-color: #161616;
  color: white;
  z-index: 1;
}

.save-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  border: 1px solid currentColor;
}

/* Remove spinner controls from number input */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

/* Mobile responsive styles */
@media (max-width: 400px) {
  .plan-buttons-container {
    flex-direction: column;
    gap: 8px;
  }
  
  .plan-selector-btn {
    min-height: 108px !important;
    min-width: 100% !important;
    padding: 12px 16px !important;
  }
  
  .plan-price {
    font-size: 22px;
  }
  
  .selection-dot {
    width: 16px;
    height: 16px;
    top: 8px;
    left: 8px;
  }
  
  .save-badge-new {
    padding: 3px 8px;
    font-size: 10px;
    top: 8px;
    right: 8px;
  }
}

@media (min-width: 401px) and (max-width: 600px) {
  .plan-selector-btn {
    min-height: 108px !important;
    padding: 12px 8px !important;
  }
  
  .plan-price {
    font-size: 20px;
  }
  
  .plan-label {
    font-size: 11px;
  }
  
  .plan-period {
    font-size: 9px;
  }
  
  .selection-dot {
    width: 14px;
    height: 14px;
    top: 6px;
    left: 6px;
  }
  
  .save-badge-new {
    padding: 2px 5px;
    font-size: 8px;
    top: 4px;
    right: 4px;
  }
}
</style>
